import * as React from 'react';
import Text from '../text';
import Checkbox from '../checkbox';

import ChevronRight from '../icons/24/arrow-right';
import ChevronUp from '../icons/24/arrow-up';

import { CheckboxCategory as Props } from './form-content.checkbox-list.types';
import VisuallyHidden from '../visually-hidden';
import { mod } from '../add-bem-modifiers';
import Collapse from '../collapse';

const CheckboxCategory: React.FC<React.PropsWithChildren<Props>> = ({
  currentlySelectedValues = [],
  id,
  isSearching,
  options,
  searchResults = [],
  selectAll,
  selectAllLabel,
  setMultipleSelectedValues = () => {},
  setSelectedValue = () => {},
  shouldShowItems = true,
  removeAllValues = () => {},
}) => {
  const isExpandable = React.useMemo(
    () => (selectAll ? true : false),
    [selectAll],
  );
  const [availableOptions] = React.useState(
    options.filter(button => !button.disabled),
  );

  const [isOpen, setIsOpen] = React.useState(isExpandable ? true : false);

  const handleChange = (value: string) => {
    setSelectedValue(id, value);
  };

  const handleSelectAll = () => {
    if (currentlySelectedValues.length !== availableOptions.length) {
      setMultipleSelectedValues(
        id,
        availableOptions.map(button => button.value),
      );
    } else {
      removeAllValues(id);
    }
  };

  React.useEffect(
    () =>
      isSearching
        ? setIsOpen(true)
        : isExpandable
          ? setIsOpen(false)
          : setIsOpen(true),
    [isExpandable, isSearching, searchResults],
  );

  const disableCategory = options.every(checkbox => checkbox.disabled);

  const hasSelectedValues = currentlySelectedValues.length > 0;

  return (
    <fieldset className="form-content__fieldset">
      {isExpandable && (
        <div
          className={mod('form-content__chevron-button-wrapper', {
            hidden: isSearching,
          })}
        >
          {selectAll ? (
            <div className="form-content__select-all">
              <Checkbox
                {...selectAll}
                isLabelVisible={false}
                checked={
                  availableOptions.length > 0 &&
                  currentlySelectedValues.length === availableOptions.length
                }
                onChange={handleSelectAll}
                data-test-select-all
                disabled={disableCategory}
              />
            </div>
          ) : null}
          <button
            className="form-content__chevron-button"
            onClick={() => setIsOpen(currentState => !currentState)}
            type="button"
          >
            <Text
              elementName="span"
              className={mod('form-content__select-all-label', {
                disabled: disableCategory,
              })}
              size={Text.sizes.basic}
            >
              {selectAllLabel}
            </Text>
            <div className="form-content__checkbox-category-info-wrapper">
              {hasSelectedValues ? (
                <Text
                  className="form-content__checkbox-category-number"
                  size={Text.sizes.interface}
                >
                  {currentlySelectedValues.length}
                </Text>
              ) : undefined}
              {isOpen ? (
                <div className="form-content__checkbox-category-icon">
                  <ChevronUp />
                </div>
              ) : (
                <div className="form-content__checkbox-category-icon">
                  <ChevronRight />
                </div>
              )}
            </div>
          </button>
        </div>
      )}

      <Collapse
        duration={300}
        animateChildren={isExpandable ? true : false}
        isOpen={isOpen}
      >
        {
          <fieldset className="form-content__fieldset">
            {options.map(checkbox => {
              const isInSearchResults = isSearching
                ? searchResults.find(
                    item =>
                      item.key.toLowerCase() === checkbox.value.toLowerCase(),
                  )
                : undefined;

              const isSelected = currentlySelectedValues.includes(
                checkbox.value,
              );

              const node = (
                <div
                  key={checkbox.value}
                  className={mod('form-content__checkbox-button', {
                    nested: !isSearching && isExpandable,
                  })}
                >
                  <Checkbox
                    onChange={e => handleChange(e.target.value)}
                    checked={isSelected}
                    isUnderlined={true}
                    {...checkbox}
                  />
                </div>
              );

              if (!isSelected && !isInSearchResults && !shouldShowItems) {
                return;
              }

              if (isSelected && isSearching && !isInSearchResults) {
                return (
                  <VisuallyHidden key={checkbox.value}>{node}</VisuallyHidden>
                );
              }

              if (!isSelected && isSearching && !isInSearchResults) {
                return;
              }

              return node;
            })}
          </fieldset>
        }
      </Collapse>
    </fieldset>
  );
};

export default CheckboxCategory;
